import { useState, useEffect } from "react";
import axios from "axios";
import { Users, Bell } from "lucide-react";
import PartyCard from "../components/PartyCard";
import PartyDetailModal from "../components/PartyDetailModal";
import ToolsGrid from "../components/ToolsGrid";
import { useSACountdown, useVICCountdown, useNSWCountdown } from "../hooks/useElectionCountdowns";
import { useTheme } from "../context/ThemeContext";
import { HomeHero } from "../components/home/HomeHero";
import { Federal2025Banner } from "../components/home/Federal2025Banner";
import { ElectionSpotlightBanner } from "../components/home/ElectionSpotlightBanner";
import { UpcomingElectionsGrid } from "../components/home/UpcomingElectionsGrid";
import { ElectionNotificationsSection } from "../components/home/ElectionNotificationsSection";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TABS = ["Federal", "NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];

const getDaysUntil = (d) => Math.ceil((new Date(d) - new Date()) / 864e5);

export default function HomePage() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [activeTab, setActiveTab] = useState("Federal");
  const [parties, setParties] = useState([]);
  const [upcomingElections, setUpcomingElections] = useState([]);
  const [selectedParty, setSelectedParty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [partyPulses, setPartyPulses] = useState({});
  const [pulsesLoading, setPulsesLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [reminderStatus, setReminderStatus] = useState("");
  const [selectedElections, setSelectedElections] = useState([]);
  const [pushPermission, setPushPermission] = useState(() =>
    typeof Notification !== "undefined" ? Notification.permission : "unsupported"
  );
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);

  const bg = isDark ? "#080E1A" : "#F8FAFC";
  const surface = isDark ? "#1E293B" : "#FFFFFF";
  const text = isDark ? "#F8FAFC" : "#0F172A";
  const textSec = isDark ? "#94A3B8" : "#475569";
  const borderC = isDark ? "rgba(51,65,85,0.55)" : "rgba(226,232,240,0.85)";
  const shadowMd = isDark ? "0 4px 20px -2px rgba(0,0,0,0.35)" : "0 4px 16px -2px rgba(15,23,42,0.07)";
  const saCountdown  = useSACountdown();
  const vicCountdown = useVICCountdown();
  const nswCountdown = useNSWCountdown();

  useEffect(() => { fetchUpcomingElections(); }, []);
  useEffect(() => {
    const type = activeTab === "Federal" ? "federal" : activeTab;
    fetchParties(type);
  }, [activeTab]);

  const fetchUpcomingElections = async () => {
    try {
      const res = await axios.get(`${API}/elections/upcoming`);
      setUpcomingElections(res.data);
    } catch {}
  };

  const fetchParties = async (type) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/parties`, { params: { election_type: type } });
      setParties(res.data);
      fetchPartyPulses(res.data);
    } catch {} finally { setLoading(false); }
  };

  const fetchPartyPulses = async (partiesList) => {
    if (!partiesList || partiesList.length === 0) return;
    setPulsesLoading(true);
    try {
      const results = await Promise.allSettled(
        partiesList.map((p) => axios.post(`${API}/ai/party-content`, { party_id: p.id, party_name: p.name, party_description: p.description, content_type: "pulse" }))
      );
      const map = {};
      results.forEach((r, i) => { if (r.status === "fulfilled") map[partiesList[i].id] = r.value.data.content; });
      setPartyPulses(map);
    } catch {} finally { setPulsesLoading(false); }
  };

  const submitReminder = async () => {
    if (!email || !email.includes("@")) { setReminderStatus("Please enter a valid email."); return; }
    if (selectedElections.length === 0) { setReminderStatus("Select at least one election."); return; }
    try {
      await axios.post(`${API}/subscriptions`, { email, election_ids: selectedElections });
      setReminderStatus("Subscribed! We'll remind you before election day.");
      setEmail(""); setSelectedElections([]);
    } catch { setReminderStatus("Failed to subscribe. Please try again."); }
  };

  const enablePushNotifications = async () => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
    setPushLoading(true);
    try {
      const permission = await Notification.requestPermission();
      setPushPermission(permission);
      if (permission !== "granted") { setPushLoading(false); return; }
      const reg = await navigator.serviceWorker.ready;
      const { data } = await axios.get(`${API}/push/vapid-key`);
      const pad = "=".repeat((4 - (data.public_key.length % 4)) % 4);
      const b64 = (data.public_key + pad).replace(/-/g, "+").replace(/_/g, "/");
      const rawData = window.atob(b64);
      const appKey = Uint8Array.from(rawData, (c) => c.charCodeAt(0));
      const subscription = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: appKey });
      await axios.post(`${API}/push/subscribe`, subscription.toJSON());
      setPushSubscribed(true);
    } catch (e) { console.error("Push subscription failed:", e); } finally { setPushLoading(false); }
  };

  const disablePushNotifications = async () => {
    setPushLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) { await axios.post(`${API}/push/unsubscribe`, { endpoint: sub.endpoint }); await sub.unsubscribe(); }
      setPushSubscribed(false);
    } catch (e) { console.error("Unsubscribe failed:", e); } finally { setPushLoading(false); }
  };

  const nextElection = upcomingElections[0];
  const daysToNext = nextElection ? getDaysUntil(nextElection.date) : null;
  const enrolDeadline = daysToNext !== null ? daysToNext - 7 : null;
  const Bell_icon = Bell;

  return (
    <div className="min-h-screen" style={{ backgroundColor: bg }}>

      {/* Enrolment Alert */}
      {enrolDeadline !== null && enrolDeadline <= 60 && (
        <div
          className="py-3 px-4"
          style={{
            background: enrolDeadline <= 7
              ? "linear-gradient(90deg, rgba(225,29,72,0.15), rgba(8,14,26,0.9))"
              : "linear-gradient(90deg, rgba(234,179,8,0.10), rgba(8,14,26,0.88))",
            borderBottom: "1px solid rgba(51,65,85,0.4)",
          }}
          data-testid="enrolment-alert"
        >
          <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Bell_icon className="w-4 h-4 flex-shrink-0" style={{ color: enrolDeadline <= 7 ? "#F43F5E" : "#FACC15" }} />
              <p className="text-sm font-medium" style={{ color: "#F8FAFC" }}>
                {enrolDeadline <= 0
                  ? `Enrolment has closed for ${nextElection.name}!`
                  : `Enrolment for ${nextElection.name} closes in ~${enrolDeadline} days`}
              </p>
            </div>
            <a href="https://www.aec.gov.au/enrol/" target="_blank" rel="noopener noreferrer" data-testid="enrol-now-link"
              className="text-xs font-semibold px-3 py-1.5 rounded-lg transition-all"
              style={{ background: enrolDeadline <= 7 ? "#F43F5E" : "#FACC15", color: enrolDeadline <= 7 ? "#fff" : "#0F172A", textDecoration: "none" }}
            >
              Check Enrolment →
            </a>
          </div>
        </div>
      )}

      <HomeHero />

      <Federal2025Banner isDark={isDark} surface={surface} text={text} textSec={textSec} />

      {/* SA 2026 Spotlight */}
      <ElectionSpotlightBanner
        countdown={saCountdown}
        testId="sa2026-banner"
        badge="SA 2026"
        isPrimary
        bgGradient="linear-gradient(135deg, #0F172A 0%, #1a0a10 50%, #0F172A 100%)"
        borderColor="rgba(244,63,94,0.20)"
        glowColor="rgba(244,63,94,0.09)"
        accentColor="#F43F5E"
        topBarGradient="linear-gradient(90deg, #E11D48, #F43F5E, transparent)"
        title="SA State Election — March 21, 2026"
        subtitle="Malinauskas (ALP) vs Speirs (Liberal) · 47 lower house seats"
        issues={["AUKUS Submarines", "Hydrogen Economy", "Cost of Living", "Housing + Health"]}
        guidePath="/sa-2026"
        guideLabel="SA 2026 Election Guide"
        extraBadges={["ELECTION GUIDE"]}
      />

      {/* VIC 2026 Spotlight */}
      <ElectionSpotlightBanner
        countdown={vicCountdown}
        testId="vic2026-banner"
        badge="VIC 2026"
        bgGradient="linear-gradient(135deg, #0F172A 0%, #111827 100%)"
        borderColor="rgba(51,65,85,0.5)"
        accentColor="#64748B"
        countdownNumColor="#94A3B8"
        topBarGradient="linear-gradient(90deg, #334155, #475569, transparent)"
        title="VIC State Election — November 28, 2026"
        subtitle="Jacinta Allan (ALP) vs John Pesutto (Liberal) · 88 lower house seats"
        issues={["State Debt & Cost of Living", "Suburban Rail Loop", "Housing Affordability", "Health & Climate"]}
        guidePath="/vic-2026"
        guideLabel="VIC 2026 Election Guide"
        extraBadges={["ELECTION GUIDE"]}
      />

      {/* NSW 2027 Spotlight */}
      <ElectionSpotlightBanner
        countdown={nswCountdown}
        testId="nsw2027-banner"
        badge="NSW 2027"
        bgGradient="linear-gradient(135deg, #0A1225 0%, #0f1e3a 100%)"
        borderColor="rgba(59,130,246,0.15)"
        accentColor="#3B82F6"
        countdownNumColor="#60A5FA"
        topBarGradient="linear-gradient(90deg, #2563EB, #3B82F6, transparent)"
        title="NSW State Election — March 27, 2027"
        subtitle="Chris Minns (ALP) vs Mark Speakman (Liberal) · 93 lower house seats"
        issues={["Housing Affordability", "Cost of Living", "Transport & Metro", "Energy Transition"]}
        guidePath="/nsw-2027"
        guideLabel="NSW 2027 Election Guide"
        extraBadges={["ELECTION GUIDE"]}
      />

      <UpcomingElectionsGrid
        upcomingElections={upcomingElections}
        isDark={isDark}
        surface={surface}
        text={text}
        textSec={textSec}
        borderC={borderC}
        shadowMd={shadowMd}
      />

      <ToolsGrid />

      {/* Party Explorer */}
      <section id="party-explorer" className="py-14 px-4" data-testid="party-explorer-section" style={{ backgroundColor: bg }}>
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: "rgba(13,92,58,0.10)", border: "1px solid rgba(13,92,58,0.18)" }}>
              <Users style={{ width: 15, height: 15, color: "#0D5C3A" }} />
            </div>
            <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Outfit, sans-serif", color: text }}>Browse Parties</h2>
          </div>
          <div className="flex gap-1.5 mb-6 overflow-x-auto pb-1" style={{ WebkitOverflowScrolling: "touch", scrollbarWidth: "none", msOverflowStyle: "none" }} data-testid="party-tabs">
            {TABS.map((tab) => {
              const isActive = activeTab === tab;
              return (
                <button key={tab} data-testid={`tab-${tab.toLowerCase()}`} onClick={() => setActiveTab(tab)}
                  className="px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 flex-shrink-0"
                  style={{
                    background: isActive ? (isDark ? "#F8FAFC" : "#0F172A") : (isDark ? "rgba(30,41,59,0.6)" : "rgba(241,245,249,0.8)"),
                    color: isActive ? (isDark ? "#0F172A" : "#F8FAFC") : textSec,
                    border: `1px solid ${isActive ? "transparent" : borderC}`,
                    fontWeight: isActive ? "600" : "500",
                  }}
                >
                  {tab}
                </button>
              );
            })}
          </div>
          {!loading && <p className="text-xs mb-5 font-medium" style={{ color: textSec }}>{parties.length} {parties.length === 1 ? "party" : "parties"} in {activeTab === "Federal" ? "Federal elections" : `${activeTab} elections`}</p>}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => <div key={i} className="h-56 animate-pulse rounded-2xl" style={{ background: isDark ? "rgba(30,41,59,0.5)" : "#E2E8F0" }} />)}
            </div>
          ) : parties.length === 0 ? (
            <div className="text-center py-16 rounded-2xl" style={{ background: surface, border: `1px solid ${borderC}` }}>
              <p className="font-medium" style={{ color: textSec }}>No parties found for this jurisdiction.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {parties.map((party) => <PartyCard key={party.id} party={party} onClick={() => setSelectedParty(party)} pulse={partyPulses[party.id]} pulseLoading={pulsesLoading} />)}
            </div>
          )}
        </div>
      </section>

      <ElectionNotificationsSection
        isDark={isDark} surface={surface} text={text} textSec={textSec} borderC={borderC} shadowMd={shadowMd}
        email={email} setEmail={setEmail}
        selectedElections={selectedElections} setSelectedElections={setSelectedElections}
        upcomingElections={upcomingElections}
        reminderStatus={reminderStatus} submitReminder={submitReminder}
        pushPermission={pushPermission} pushSubscribed={pushSubscribed} pushLoading={pushLoading}
        enablePushNotifications={enablePushNotifications} disablePushNotifications={disablePushNotifications}
      />

      {/* First-Timer CTA */}
      <section className="py-20 px-4 relative overflow-hidden" style={{ background: "linear-gradient(135deg, #080E1A 0%, #0D1628 50%, #080E1A 100%)" }} data-testid="first-voter-cta">
        <div style={{ position: "absolute", top: "20%", left: "10%", width: 400, height: 400, background: "radial-gradient(circle, rgba(234,179,8,0.07) 0%, transparent 70%)", filter: "blur(50px)", pointerEvents: "none" }} />
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 items-center relative">
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 mb-5 text-xs font-semibold tracking-widest uppercase rounded-full" style={{ background: "rgba(234,179,8,0.10)", border: "1px solid rgba(234,179,8,0.22)", color: "#FACC15" }}>
              First Time Voting?
            </div>
            <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
              Australia's voting system explained simply.
            </h2>
            <p className="text-base mb-7 leading-relaxed" style={{ color: "rgba(255,255,255,0.68)", fontFamily: "Public Sans, sans-serif" }}>
              Preferential voting, Senate ballots, polling day — we break it all down in plain English.
            </p>
            <button data-testid="first-timer-guide-btn" onClick={() => navigate("/how-to-vote")}
              className="px-8 py-3.5 font-semibold text-base rounded-xl transition-all hover:-translate-y-0.5 active:scale-[0.98]"
              style={{ background: "linear-gradient(135deg, #EAB308, #FACC15)", color: "#0F172A", boxShadow: "0 8px 24px rgba(234,179,8,0.28)" }}
            >
              First Timer's Guide →
            </button>
          </div>
          <div className="hidden md:block">
            <img src="https://images.unsplash.com/photo-1758613171146-8d15cfacc14e?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Young Australian voters" className="w-full" style={{ maxHeight: "280px", objectFit: "cover", borderRadius: 16, border: "1px solid rgba(234,179,8,0.15)" }} />
          </div>
        </div>
      </section>

      <footer className="py-8 px-4 border-t" style={{ background: isDark ? "#080E1A" : "#F8FAFC", borderColor: isDark ? "rgba(51,65,85,0.35)" : "rgba(226,232,240,0.85)" }}>
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="text-lg font-black tracking-tight" style={{ fontFamily: "Outfit, sans-serif", color: text }}>
            AusVote<span style={{ color: "#EAB308" }}>.</span>
            <span className="text-xs font-normal ml-2" style={{ color: textSec }}>Civic education for all Australians</span>
          </span>
          <div className="flex gap-5 text-xs" style={{ color: textSec }}>
            <a href="https://www.aec.gov.au" target="_blank" rel="noopener noreferrer" className="hover:text-emerald-600 font-medium transition-colors">Australian Electoral Commission</a>
            <a href="https://www.parliament.au" target="_blank" rel="noopener noreferrer" className="hover:text-emerald-600 font-medium transition-colors">Parliament of Australia</a>
          </div>
        </div>
      </footer>

      {selectedParty && <PartyDetailModal party={selectedParty} onClose={() => setSelectedParty(null)} />}
    </div>
  );
}
